/**
 * CopyButton Component
 * Small icon button with clipboard copy functionality and checkmark feedback
 */

import * as React from "react";
import { Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CopyButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick"
> {
  /** Text to copy to clipboard */
  value: string;
  /** Size variant */
  size?: "sm" | "default";
  /** Callback when copy succeeds */
  onCopy?: () => void;
  /** Callback when copy fails */
  onCopyError?: (error: Error) => void;
  /** Duration in ms to show success state (default: 2000) */
  successDuration?: number;
}

export function CopyButton({
  value,
  size = "default",
  onCopy,
  onCopyError,
  successDuration = 2000,
  className,
  title = "Copy to clipboard",
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopy?.();

      setTimeout(() => {
        setCopied(false);
      }, successDuration);
    } catch (error) {
      onCopyError?.(error instanceof Error ? error : new Error("Copy failed"));
    }
  }, [value, onCopy, onCopyError, successDuration]);

  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  const buttonSize = size === "sm" ? "h-6 w-6" : "h-7 w-7";

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title}
      className={cn(
        "inline-flex items-center justify-center rounded-md transition-all duration-150",
        "text-muted-foreground hover:text-foreground",
        "hover:bg-accent/50 active:scale-95",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-40",
        buttonSize,
        copied && "text-green-500 hover:text-green-500",
        className,
      )}
      {...props}
    >
      {copied ? (
        <Check className={cn(iconSize, "text-green-500")} />
      ) : (
        <Copy className={iconSize} />
      )}
    </button>
  );
}
